import { forwardRef, Children, cloneElement } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FormControl, FormControlProps } from "./FormControl";
import {
    COL_SPAN,
    FLEX,
    GRID_COLS,
    DIRECTION,
    GAP,
    ALIGN_ITEMS,
    JUSTIFY_CONTENT,
    COLOR_BACKGROUND,
    COLOR_TEXT,
    TEXT_ALIGN,
    PADDING,
} from "../features/foundation";

const ALIGNS = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
};

const ALIGNS_FLEX = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-rend",
};

type GroupProps = {
    children?: ReactNode;
    hidden?: boolean;
    flex?: keyof typeof FLEX;
    height?: any;
};

type GroupHeaderProps = {
    children?: ReactNode;
    hidden?: boolean;
};

type GroupTitleProps = {
    title?: string;
    hidden?: boolean;
    description?: string;
    titleSize?: 1 | 2 | 3;
    required?: boolean;
};

type GroupBodyProps = {
    children?: ReactNode;
    hidden?: boolean;
    gap?: keyof typeof GAP;
    flex?: keyof typeof FLEX;
    padding?: keyof typeof PADDING;
    height?: any;
};

type GroupSectionProps = {
    children?: ReactNode;
    hidden?: boolean;
    gap?: keyof typeof GAP;
};

type GroupFooterProps = {
    children?: ReactNode;
    hidden?: boolean;
};

type GroupRowProps = {
    children?: ReactNode;
    hidden?: boolean;
    borderTop?: boolean;
    borderBottom?: boolean;
    borderLeft?: boolean;
    borderRight?: boolean;
};

type GroupAnyProps = {
    children?: ReactNode;
    hidden?: boolean;
    align?: keyof typeof ALIGNS;
    anySize?: keyof typeof COL_SPAN;
    padding?: keyof typeof PADDING;
};

type GroupCellProps = {
    children?: ReactNode;
    hidden?: boolean;
    size?: keyof typeof COL_SPAN;
    gap?: keyof typeof GAP;
    root?: boolean;
    header?: any;
    height?: string | number;
    direction?: keyof typeof DIRECTION;
    align?: keyof typeof JUSTIFY_CONTENT;
    required?: boolean;
    backgroundColor?: keyof typeof COLOR_BACKGROUND;
    textColor?: keyof typeof COLOR_TEXT;
};

type GroupLabelProps = FormControlProps & {
    hidden?: boolean;
    label?: ReactNode;
    labelSize?: keyof typeof COL_SPAN;
    required?: boolean | string;
    align?: keyof typeof ALIGNS_FLEX;
    borderTop?: boolean;
    borderBottom?: boolean;
    borderLeft?: boolean;
    borderRight?: boolean;
};

export type GroupControlProps = GroupLabelProps & {
    hidden?: boolean;
    controlSize?: keyof typeof COL_SPAN;
    only?: "control" | "label";
    "data-parent"?: string;
    borderTop?: boolean;
    borderBottom?: boolean;
    borderLeft?: boolean;
    borderRight?: boolean;
    backgroundColor?: keyof typeof COLOR_BACKGROUND;
    textColor?: keyof typeof COLOR_TEXT;
};

type GroupColProps = GroupLabelProps & {
    hidden?: boolean;
    children?: ReactNode;
    colSize?: keyof typeof COL_SPAN;
    padding?: keyof typeof PADDING;
    combine?: boolean;
    as?: "link";
    onClick?: any;
};

/**
 *
 * # Group
 * * Header
 * * Title
 * * Body
 * * Section
 * * Row
 * * Label
 * * Control
 * * Col
 * * Cell
 * @param props GroupProps
 * @returns
 */

const Group = (props: GroupProps) => {
    const { children, hidden, flex, height } = props;
    return (
        <div
            hidden={hidden}
            aria-hidden={hidden}
            className={classNames(
                "flex-col rounded bg-card shadow w-full h-fit",
                flex && FLEX[flex],
                !hidden && "flex",
            )}
            style={{ height }}
        >
            {children}
        </div>
    );
};

const GroupHeader = (props: GroupHeaderProps) => {
    const { children, hidden } = props;

    return (
        <div hidden={hidden} className="p-4 rounded-t bg-header">
            {children}
        </div>
    );
};

const GroupTitle = (props: GroupTitleProps) => {
    const { title, description, titleSize = 1, hidden, required } = props;
    const { t } = useTranslation();
    const sizes = { 3: "text-lg", 2: "text-xl", 1: "text-2xl" };

    return (
        <div hidden={hidden}>
            {title && (
                <div className={classNames("font-semibold", sizes[titleSize])}>
                    {t(title)}
                    {required && <span className="text-error ml-0.5">*</span>}
                </div>
            )}
            {description && <p>{t(description)}</p>}
        </div>
    );
};

const GroupBody = (props: GroupBodyProps) => {
    const { children, hidden, gap = 0, height, flex, padding = 4 } = props;
    return (
        <div
            hidden={hidden}
            className={classNames(
                "flex-col w-full flex-1 space-y-4 overflow-auto",
                PADDING[padding],
                GAP[gap],
                !hidden && "flex",
                flex && FLEX[flex],
            )}
            style={{ height }}
        >
            {children}
        </div>
    );
};

const GroupSection = (props: GroupSectionProps) => {
    const { children, hidden, gap = 0 } = props;

    return (
        <section
            ref={(node) => {
                if (!node) return;
                let done;
                for (const child of Array.from(node.children)) {
                    if (child.classList.contains("uf-group-cell") || child.classList.contains("uf-group-row")) {
                        if (!child.hasAttribute("hidden")) {
                            if (done) {
                                child.classList.remove("border-t");
                                return;
                            }

                            if (!done) {
                                child.classList.add("border-t");
                                done = true;
                            }
                        }
                    }
                }
            }}
            hidden={hidden}
            className={classNames("w-full flex-col", GAP[gap], !hidden && "flex")}
        >
            {children}
        </section>
    );
};

const GroupFooter = (props: GroupFooterProps) => {
    const { children, hidden } = props;
    return (
        <div hidden={hidden} className="p-4 rounded-b bg-header">
            {children}
        </div>
    );
};

const GroupCell = (props: GroupCellProps) => {
    const {
        children,
        root,
        size = root ? 12 : 2,
        header,
        height,
        gap = 0,
        direction = "row",
        align = "start",
        required,
        hidden,
        backgroundColor,
        textColor,
    } = props;

    const { t } = useTranslation();
    const end = (Array.isArray(children) ? children : [children]).some((_) => {
        return _?.type?.displayName !== "GroupCell";
    });

    return (
        <div
            hidden={hidden}
            className={classNames(
                "uf-group-cell min-h-[2.5rem] break-all",
                COL_SPAN[size],
                root && "border-x border-b",
                header && "bg-header font-semibold text-center",
                !end && GRID_COLS[size],
                !end && "bg-border",
                !end && "gap-[1px]",
                end && "p-1",
                end && direction === "row" && "items-center",
                end && !header && "bg-card",
                end && GAP[gap],
                end && DIRECTION[direction],
                end && JUSTIFY_CONTENT[align],
                !hidden && (end ? "flex" : "grid"),
                backgroundColor && COLOR_BACKGROUND[backgroundColor],
                textColor && COLOR_TEXT[textColor],
            )}
            style={{ height }}
        >
            {Children.map(children, (child: any) => {
                if (typeof child === "string") return t(child);
                if (child) return cloneElement(child);
            })}
            {required && <span className={classNames("text-error ml-0.5")}>*</span>}
        </div>
    );
};

GroupCell.displayName = "GroupCell";

const GroupRow = (props: GroupRowProps) => {
    const { hidden, borderLeft = true, borderRight = true, borderTop = true, borderBottom = true, children } = props;
    return (
        <div
            hidden={hidden}
            className={classNames(
                "uf-group-row w-full grid-cols-12 min-h-[2.5rem] border-x border-b first:border-t sm:grid-cols-1",
                borderLeft === false && "border-l-0",
                borderRight === false && "border-r-0",
                borderTop === false && "border-t-0",
                borderBottom === false && "border-b-0",
                !hidden && "grid",
            )}
        >
            {children}
        </div>
    );
};

const GroupLabel = forwardRef((props: GroupLabelProps, ref) => {
    const { t } = useTranslation();
    const {
        hidden,
        label,
        labelSize = 2,
        align = "left",
        borderLeft = true,
        borderRight = true,
        borderTop = true,
        borderBottom = true,
        ...rest
    } = props;

    return (
        <div
            hidden={hidden}
            className={classNames(
                "font-semibold relative items-center p-1 break-all bg-header md:border-x lg:border-x first:border-l-0 last:border-r-0 first:last:border-r",
                COL_SPAN[labelSize],
                ALIGNS_FLEX[align],
                borderLeft === false && "border-l-0",
                borderRight === false && "border-r-0",
                borderTop === false && "border-t-0",
                borderBottom === false && "border-b-0",
                !hidden && "flex",
            )}
        >
            {props.type ? <FormControl ref={ref} {...rest} /> : typeof label === "string" ? t(label) : label}
            {props.required && (
                <span
                    className={classNames("text-error ml-0.5", {
                        "absolute top-0 right-0.5": props.type,
                    })}
                >
                    *
                </span>
            )}
        </div>
    );
});

const GroupAny = (props: GroupAnyProps) => {
    const { children, hidden, anySize, padding = 1, align = "center", ...rest } = props;
    return (
        <>
            <div
                hidden={hidden}
                className={classNames(
                    "items-center",
                    "space-x-1",
                    !hidden && "flex",
                    anySize === undefined ? "min-w-fit" : COL_SPAN[anySize],
                    PADDING[padding],
                )}
                {...rest}
            >
                <div className={classNames("w-full", ALIGNS[align])}>{children}</div>
            </div>
        </>
    );
};

const GroupControl = forwardRef((props: GroupControlProps, ref) => {
    const {
        hidden,
        labelSize,
        label,
        only,
        controlSize = 4,
        borderLeft = false,
        borderRight = false,
        borderTop = false,
        borderBottom = false,
        backgroundColor,
        textColor,
        ...rest
    } = props;

    const align: Record<string, "start" | "center" | "end"> = {
        left: "start",
        center: "center",
        right: "end",
    };

    return (
        <>
            {!props["data-parent"] && label !== undefined && only !== "control" && (
                <GroupLabel required={props.required} label={label} labelSize={labelSize} />
            )}
            {props["data-parent"] === "group_col" ? (
                <FormControl ref={ref} backgroundColor={backgroundColor} {...rest} />
            ) : (
                <div
                    className={classNames(
                        "flex items-center p-1",
                        COL_SPAN[controlSize],
                        borderLeft === false ? "border-l-0" : "border-l-[1px]",
                        borderRight === false ? "border-r-0" : "border-r-[1px]",
                        borderTop === false ? "border-t-0" : "border-t-[1px]",
                        borderBottom === false ? "border-b-0" : "border-b-[1px]",
                        backgroundColor && COLOR_BACKGROUND[backgroundColor],
                        textColor && COLOR_TEXT[textColor],
                        rest.align && JUSTIFY_CONTENT[align[rest.align]],
                    )}
                >
                    <FormControl ref={ref} {...rest} />
                </div>
            )}
        </>
    );
});

const GroupCol = (props: GroupColProps) => {
    const { children, required, label, labelSize, combine = false, colSize = 4, padding = 1, as, onClick } = props;
    return (
        <>
            {label && <GroupLabel required={required} label={label} labelSize={labelSize} />}

            {combine ? (
                <div
                    className={classNames(
                        "flex items-center p-1",
                        COL_SPAN[colSize],
                        as === "link" &&
                            "[&_.control-text]:text-blue [&_.control-text]:underline [&_.control-text]:cursor-pointer",
                    )}
                    onClick={onClick}
                >
                    <div className="flex border rounded divide-x overflow-hidden w-full">
                        {Children.map(children, (child: any) => {
                            return (
                                <div
                                    className={classNames(
                                        "[&_*]:border-none [&_*]:rounded-none",
                                        child?.props?.size === "full" && "flex-1",
                                    )}
                                >
                                    {child && cloneElement(child, { "data-parent": "group_col" })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div
                    className={classNames(
                        " flex items-center space-x-1",
                        PADDING[padding],
                        COL_SPAN[colSize],
                        as === "link" &&
                            "[&_.control-text]:text-blue [&_.control-text]:underline [&_.control-text]:cursor-pointer",
                    )}
                    onClick={onClick}
                >
                    {Children.map(children, (child: any) => {
                        if (typeof child === "string") return child;
                        if (child) return cloneElement(child, { "data-parent": "group_col" });
                    })}
                </div>
            )}
        </>
    );
};

Group.Header = GroupHeader;
Group.Body = GroupBody;
Group.Footer = GroupFooter;
Group.Section = GroupSection;
Group.Title = GroupTitle;
Group.Row = GroupRow;
Group.Col = GroupCol;
Group.Field = GroupCol;
Group.Any = GroupAny;
Group.Label = GroupLabel;
Group.Control = GroupControl;

Group.Cell = GroupCell;

export default Group;
